import type { CreatorCollaborationPackage } from '@/biz/multiPlatformCollaboration/model';

export type PackageRates = Record<string, number>;

const MONEY_SCALE = 100;
const toMoneyCents = (value: number) => Math.round(value * MONEY_SCALE);

export const capCreatorPackageRateAtChannelTotal = ({
  packageRate,
  channelTotal
}: {
  packageRate: number;
  channelTotal: number;
}) => {
  if (!Number.isFinite(packageRate)) return packageRate;
  const channelTotalCents = Number.isFinite(channelTotal)
    ? Math.max(0, toMoneyCents(channelTotal))
    : 0;
  if (channelTotalCents <= 0) return packageRate;
  return Math.min(toMoneyCents(packageRate), channelTotalCents) / MONEY_SCALE;
};

export const normalizeMoneyInputDraft = (value: string) => {
  const cleaned = value.replace(/[^\d.]/g, '');
  if (!cleaned) return '';

  const [whole = '', ...decimalParts] = cleaned.split('.');
  const normalizedWhole = whole.replace(/^0+(?=\d)/, '');
  if (!cleaned.includes('.')) return normalizedWhole;

  return `${normalizedWhole || '0'}.${decimalParts.join('').slice(0, 2)}`;
};

export type CreatorPackageRateValidation =
  | { isValid: true }
  | {
      isValid: false;
      reason: 'PACKAGE_RATE_REQUIRED' | 'PACKAGE_ABOVE_CHANNEL_TOTAL';
      maximum?: number;
    };

/**
 * Creator package rules shared by the initial quote and every counteroffer.
 * Individual channel rates have no negotiation range; only the package total
 * is capped. The backend repeats the same checks and returns 47016 as a guard.
 */
export const validateCreatorPackageRate = ({
  packageRate,
  channelTotal
}: {
  packageRate: number;
  channelTotal: number;
}): CreatorPackageRateValidation => {
  if (!Number.isFinite(packageRate) || packageRate <= 0) {
    return { isValid: false, reason: 'PACKAGE_RATE_REQUIRED' };
  }

  const normalizedChannelTotal = Number.isFinite(channelTotal)
    ? Math.max(0, channelTotal)
    : 0;
  if (toMoneyCents(packageRate) <= toMoneyCents(normalizedChannelTotal)) {
    return { isValid: true };
  }

  return {
    isValid: false,
    reason: 'PACKAGE_ABOVE_CHANNEL_TOTAL',
    maximum: normalizedChannelTotal
  };
};

export const getPackageRateTotal = (
  collaborationPackage: CreatorCollaborationPackage,
  rates: PackageRates
) =>
  collaborationPackage.placements.reduce(
    (sum, placement) => sum + toMoneyCents(Number(rates[placement.id] ?? 0)),
    0
  ) / MONEY_SCALE;

export const isPackageRateAllocationConsistent = (
  collaborationPackage: CreatorCollaborationPackage,
  rates: PackageRates,
  expectedTotal = collaborationPackage.packageRate
) =>
  expectedTotal === undefined ||
  toMoneyCents(getPackageRateTotal(collaborationPackage, rates)) ===
    toMoneyCents(expectedTotal);

export const arePackageRatesValid = (
  collaborationPackage: CreatorCollaborationPackage,
  rates: PackageRates,
  { allowZero = false }: { allowZero?: boolean } = {}
) =>
  collaborationPackage.placements.every((placement) => {
    const rate = Number(rates[placement.id]);
    return Number.isFinite(rate) && (allowZero ? rate >= 0 : rate > 0);
  });
